import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Button, toast } from '@zawarski/eti-ui';
import { Trash2 } from 'lucide-react';
import EntryPageShell, { EntryContent } from '@components/EntryPageShell';
import ConfirmDeleteDialog from '@components/dialogs/ConfirmDeleteDialog';
import ConfirmDiscardDialog from '@components/dialogs/ConfirmDiscardDialog';
import NotificationContactForm, {
  emptyNotification,
  toBody,
  toFormValues,
  validateNotification,
  type NotificationFormValues,
} from '@components/NotificationContactForm';
import { PageError, PageLoading } from '@components/layout/PageScaffold';
import { useAccess } from '@hooks/useAccess';
import { errorMessage } from '@utils/index';
import type { NotificationDomain } from './domain';
import { useNotificationDomain } from './useNotificationDomain';

const NotificationEdit = ({
  domain,
  mode,
}: {
  domain: NotificationDomain;
  mode: 'new' | 'edit';
}) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const rowId = mode === 'edit' ? Number(id) : 0;
  const { isStateUser, canEdit } = useAccess();
  const d = useNotificationDomain(domain, rowId || undefined);
  const [values, setValues] = useState<NotificationFormValues>(emptyNotification);
  const [initial, setInitial] = useState<NotificationFormValues>(emptyNotification);
  const [saveAttempted, setSaveAttempted] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Legacy edit page: a Delete button at the bottom of the form.
  const remove = async () => {
    try {
      await d.remove(rowId);
      toast.success('Successfully deleted notification entry. ');
      navigate(d.listPath);
    } catch (e) {
      toast.error(errorMessage(e, 'Error deleting notification.'));
    }
  };

  useEffect(() => {
    if (mode === 'edit' && d.row) {
      const next = toFormValues(d.row);
      setValues(next);
      setInitial(next);
    }
  }, [mode, d.row]);

  const errors = useMemo(() => validateNotification(values, domain), [values, domain]);
  const dirty = JSON.stringify(values) !== JSON.stringify(initial);
  const canWrite = canEdit && (domain === 'equipment' || isStateUser);
  const backTo = mode === 'edit' ? d.viewPath(rowId) : d.listPath;

  const close = () => (dirty ? setConfirmDiscard(true) : navigate(backTo));

  const save = async () => {
    setSaveAttempted(true);
    if (Object.keys(errors).length) return;
    try {
      const saved =
        mode === 'edit' ? await d.update(rowId, toBody(values)) : await d.create(toBody(values));
      toast.success(
        mode === 'edit'
          ? 'Successfully updated notification entry. '
          : 'Successfully created notification entry. ',
      );
      // Legacy: create returns to the list, update to the view.
      navigate(mode === 'edit' ? d.viewPath(saved.pvDataID) : d.listPath);
    } catch (e) {
      toast.error(
        errorMessage(
          e,
          mode === 'edit' ? 'Error updating notification.' : 'Error creating notification.',
        ),
      );
    }
  };

  if (!canWrite) {
    return (
      <EntryPageShell title='Not allowed' onBack={() => navigate(d.listPath)} backIcon='close'>
        <PageError message='You do not have permission to manage notification contacts.' />
      </EntryPageShell>
    );
  }

  return (
    <EntryPageShell
      title={mode === 'edit' ? 'Edit Notification' : 'Add Notification'}
      subtitle={d.config.title}
      onBack={close}
      backIcon='close'
      footer={
        <>
          {mode === 'edit' ? (
            <Button
              variant='destructive'
              startIcon={<Trash2 className='rep:size-4' />}
              onClick={() => setConfirmDelete(true)}>
              Delete
            </Button>
          ) : (
            <span />
          )}
          <div className='rep:flex rep:items-center rep:gap-2'>
            <Button variant='outline' onClick={close}>
              Cancel
            </Button>
            <Button onClick={() => void save()} loading={d.saving} disabled={d.saving}>
              {mode === 'edit' ? 'Save' : 'Submit'}
            </Button>
          </div>
        </>
      }>
      {mode === 'edit' && d.rowLoading ? (
        <PageLoading />
      ) : mode === 'edit' && d.rowError ? (
        <PageError message='There was an error loading the contact' />
      ) : (
        <EntryContent>
          <NotificationContactForm
            values={values}
            onChange={setValues}
            errors={errors}
            saveAttempted={saveAttempted}
            triggerOptions={d.triggerOptions}
            triggerLabel={d.config.trigger}
          />
        </EntryContent>
      )}
      <ConfirmDeleteDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title='Delete contact?'
        message={`${values.pvFullName || 'This contact'} will no longer receive notifications.`}
        onConfirm={() => void remove()}
      />
      <ConfirmDiscardDialog
        open={confirmDiscard}
        onOpenChange={setConfirmDiscard}
        onConfirm={() => navigate(backTo)}
      />
    </EntryPageShell>
  );
};

export default NotificationEdit;
